import Tool, {
  BaseTool,
  type IToolActionInput,
  type IToolAvailableInput,
  type ToolButtonComponent,
} from './Tool';
import { isShape, isSimpleSelection } from '../objects/guards';
import type ShapeCustomPath from '../shapes/ShapeCustomPath';
import Shape from '../objects/Shape';
import paper from 'paper';
import React, { useRef } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import WhiteboardController from '../controllers/WhiteboardController';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolbarRow from '../toolbars/ToolbarRow';
import ToolButton from '../toolbars/ToolButton';
import { type Instance as TippyInstance } from 'tippy.js/index';
import Tippy, { useSingleton } from '@tippyjs/react';
import { TOOLTIP_DELAY_GENERAL } from '../constants';
import { tippyPopoverPlugin } from '../utils/tippyPopoverPlugin';
import ToolDelete from './ToolDelete';
import { type SingletonObject } from '../types';

export enum BooleanOperations {
  Unite = 'unite',
  Subtract = 'subtract',
  Intersect = 'intersect',
  Exclude = 'exclude',
}

const iconForBooleanOperation = (op: BooleanOperations) => {
  switch (op) {
    case BooleanOperations.Unite:
      return 'fa-regular fa-object-union';
    case BooleanOperations.Subtract:
      return 'fa-regular fa-object-subtract';
    case BooleanOperations.Intersect:
      return 'fa-regular fa-object-intersect';
    case BooleanOperations.Exclude:
      return 'fa-regular fa-object-exclude';
  }
};

const booleanOperationButton = (
  tool: BaseTool,
  icon: string,
  tooltipContent: React.ReactNode,
  tooltipPlacement: string | undefined,
  tippySingletonTarget: SingletonObject
) => {
  return ApplicationView(() => {
    const controller = useController(WhiteboardController);

    return (
      <Tippy content={tooltipContent} singleton={tippySingletonTarget}>
        <span>
          <ToolButton onClick={() => controller.actionApplyTool(tool)}>
            <aha-icon icon={icon} />
          </ToolButton>
        </span>
      </Tippy>
    );
  });
};

class ToolBooleanUnite extends BaseTool {
  static toolName = 'Unite';

  static action({ controller, selection }) {
    ToolBooleanShape.action({
      controller,
      selection,
      booleanOperation: BooleanOperations.Unite,
    });
  }

  static buttonComponent(tippySingletonTarget: SingletonObject) {
    return booleanOperationButton(
      ToolBooleanUnite,
      iconForBooleanOperation(BooleanOperations.Unite),
      'Unite',
      'top',
      tippySingletonTarget
    );
  }
}

class ToolBooleanSubtract extends BaseTool {
  static toolName = 'Subtract';

  static action({ controller, selection }) {
    ToolBooleanShape.action({
      controller,
      selection,
      booleanOperation: BooleanOperations.Subtract,
    });
  }

  static buttonComponent(tippySingletonTarget: SingletonObject) {
    return booleanOperationButton(
      ToolBooleanSubtract,
      iconForBooleanOperation(BooleanOperations.Subtract),
      'Subtract',
      'top',
      tippySingletonTarget
    );
  }
}

class ToolBooleanIntersect extends BaseTool {
  static toolName = 'Intersect';

  static action({ controller, selection }) {
    ToolBooleanShape.action({
      controller,
      selection,
      booleanOperation: BooleanOperations.Intersect,
    });
  }

  static buttonComponent(tippySingletonTarget: SingletonObject) {
    return booleanOperationButton(
      ToolBooleanIntersect,
      iconForBooleanOperation(BooleanOperations.Intersect),
      'Intersect',
      'top',
      tippySingletonTarget
    );
  }
}

class ToolBooleanExclude extends BaseTool {
  static toolName = 'Exclude';

  static action({ controller, selection }) {
    ToolBooleanShape.action({
      controller,
      selection,
      booleanOperation: BooleanOperations.Exclude,
    });
  }

  static buttonComponent(tippySingletonTarget: SingletonObject) {
    return booleanOperationButton(
      ToolBooleanExclude,
      iconForBooleanOperation(BooleanOperations.Exclude),
      'Exclude',
      'top',
      tippySingletonTarget
    );
  }
}

const Popup: React.FC = ApplicationView(() => {
  const [tippySingletonSource, tippySingletonTarget] = useSingleton();

  const BooleanUnite = ToolBooleanUnite.buttonComponent(tippySingletonTarget);
  const BooleanSubtract =
    ToolBooleanSubtract.buttonComponent(tippySingletonTarget);
  const BooleanIntersect =
    ToolBooleanIntersect.buttonComponent(tippySingletonTarget);
  const BooleanExclude =
    ToolBooleanExclude.buttonComponent(tippySingletonTarget);

  return (
    <ToolbarMenu>
      <Tippy
        singleton={tippySingletonSource}
        delay={TOOLTIP_DELAY_GENERAL}
        zIndex={12000}
        plugins={[tippyPopoverPlugin]}
      />

      <ToolbarRow>
        <BooleanUnite />
        <BooleanSubtract />
        <BooleanIntersect />
        <BooleanExclude />
      </ToolbarRow>
    </ToolbarMenu>
  );
});

class ToolBooleanShape extends Tool {
  static toolName = 'Boolean operations';
  static icon = 'fa-regular fa-object-intersect';

  static action({
    controller,
    selection,
    booleanOperation,
  }: IToolActionInput & { booleanOperation: BooleanOperations }) {
    const objects = selection
      .getObjects()
      .filter(isShape)
      .sort((a, b) => a.z - b.z);

    // For union and intersect, use the style of the object with the highest
    // z-index, otherwise use the lowest. This is subjective, but it feels
    // right for the most common use cases.
    const objectForStyle =
      objects[
        [BooleanOperations.Unite, BooleanOperations.Intersect].includes(
          booleanOperation
        )
          ? objects.length - 1
          : 0
      ];

    // Save shape styles
    const { fill, stroke, strokeType, strokeWidth, opacity, borderOpacity } =
      objectForStyle;

    // Save text styles. If textContent for this object doesn't exist, we'll
    // use the first textContent that exists from other objects in the operation.
    let { textContent, fontSize } = objectForStyle;
    let { textAlign, verticalAlign } = objectForStyle;

    const deleteObjects = (
      controller: WhiteboardController,
      objects: fabric.Object[]
    ) => {
      controller.canvas.discardActiveObject();
      if (objects.length) {
        ToolDelete.deleteObjects(objects, controller);
        controller.canvas.remove(...objects);
      }
    };

    // TODO: After the boolean operation, the shape always ends up -0.5px to
    // the left and -0.5px to the top of the intended position
    // (CJW: This may no longer be true after I refactored the code below.)
    const paperPaths = objects.reduce<paper.PathItem[]>((acc, cur) => {
      const path = cur.shape.positionedPaperPath();
      acc.push(path);

      if (!textContent && cur.textContent) {
        textContent = cur.textContent;
        fontSize = cur.fontSize;
        textAlign = cur.textAlign;
        verticalAlign = cur.verticalAlign;
      }

      return acc;
    }, []);

    const finalPath = paperPaths.reduce((acc, cur) =>
      acc[booleanOperation](cur)
    );

    const left = finalPath.bounds.topLeft.x;
    const top = finalPath.bounds.topLeft.y;
    const width = finalPath.bounds.width;
    const height = finalPath.bounds.height;

    if (!finalPath.pathData) {
      // If the path is empty, delete the selected objects but don't create
      // the custom shape. This could happen if applying intersect to shapes
      // that don't overlap, for example.
      deleteObjects(controller, objects);
      this.toolActionComplete({ controller });
      return;
    }

    finalPath.position = new paper.Point(0, 0);

    const booleanShape = Shape.create<ShapeCustomPath>({
      controller,
      shapeName: 'customPath',
      left,
      top,
      width,
      height,
      fill,
      stroke,
      strokeType,
      strokeWidth,
      opacity,
      borderOpacity,
      angle: 0,
      scaleX: 1,
      scaleY: 1,
      shapePath: finalPath.pathData,
      textContent,
      fontSize,
      verticalAlign,
      textAlign,
    });

    deleteObjects(controller, objects);

    controller.canvas.setActiveObject(booleanShape);
    controller.canvas.requestRenderAll();

    controller.actionShowObjectToolbar();
    this.toolActionComplete({ controller });
  }

  static available({ selection }: IToolAvailableInput) {
    return !!(
      selection &&
      isSimpleSelection(selection) &&
      !selection.isLocked &&
      selection.size() >= 2 &&
      selection
        .getObjects()
        .every(obj => isShape(obj) && obj.shape.booleanOperationCompatible)
    );
  }

  static buttonComponent() {
    const Button: ToolButtonComponent = ApplicationView(({ tool }) => {
      const tippyRef = useRef<TippyInstance>();

      return (
        <ToolButton
          popup={<Popup />}
          tippyRef={tippyRef}
          id='booleanOperation'
          tool={tool}
        >
          <aha-icon icon={ToolBooleanShape.icon} />
        </ToolButton>
      );
    });

    return Button;
  }
}

export default ToolBooleanShape;
